// ── Service da Agenda (núcleo/aplicação) ──────────────────────────
// Regras/validações e transformações do domínio. Recebe o repositório via
// porta (DI) — não conhece Supabase. Toda query/RPC/insert/webhook fica no
// adapter (SupabaseAgendaRepository). Wiring concreto em src/app/agenda.ts.

import type { Json } from '@/types/database.types';
import type { AgendaProfile, AgendaMeeting, WonCard } from '@/types/agenda';
import type {
  IAgendaRepository,
  GetMeetingsByWeekParams,
  GetAvailableSlotsParams,
  AvailableSlot,
  ScheduleMeetingParams,
  UpdateMeetingLinkParams,
  EditMeetingScheduleParams,
  SwapMeetingCloserParams,
  SwapMeetingCloserResult,
  DiscardRescheduleParams,
  HandoffLeadToAgendaParams,
  PendingAgendaMeetingsParams,
  PendingReschedulesParams,
  PendingRescheduleRow,
  ConfirmPresenceParams,
  CancelMeetingParams,
  RegisterNoShowParams,
  RegisterResultParams,
  CloserScheduleRow,
  CloserScheduleInsert,
  BlockedDateRow,
  BlockedDateInsert,
  SchedulePermRow,
  SchedulePermWithEditor,
  AddSchedulePermParams,
  AgendaConfig,
  SdrCardForLead,
  CloserPipeline,
  UpdateMeetingStatusDirectParams,
  FindCloserPipelineParams,
  PipelineRow,
} from '../ports/IAgendaRepository';

const SLOT_TIMES_DEFAULT = ['09:30', '11:20', '14:20', '16:30'];

export class AgendaService {
  constructor(private readonly repo: IAgendaRepository) {}

  // ── Config: leitura com override por pipeline (`<key>:<pipelineId>`) ──
  readConfigWithPipelineOverride = async (
    workspaceId: string,
    baseKey: string,
    pipelineId: string | null,
  ): Promise<Json | undefined> => {
    const scopedKey = pipelineId ? `${baseKey}:${pipelineId}` : null;
    const keys = scopedKey ? [scopedKey, baseKey] : [baseKey];
    const rows = await this.repo.getConfigValues(workspaceId, keys);
    const map: Record<string, Json> = {};
    rows.forEach(r => { map[r.key] = r.value; });
    if (scopedKey && map[scopedKey] !== undefined && map[scopedKey] !== null) return map[scopedKey];
    return map[baseKey];
  };

  setAgendaConfig = async (workspaceId: string | null, key: string, value: Json): Promise<void> => {
    if (!workspaceId || !key) throw new Error('workspaceId e key obrigatórios.');
    await this.repo.setConfig(workspaceId, key, value);
  };

  // Helper interno — pipeline_members → profiles ativos (dedup + sort).
  listProfilesByPipelineRoleScoped = async (pipelineId: string, role: string): Promise<AgendaProfile[]> => {
    const rows = await this.repo.listPipelineMembers(pipelineId, role);
    const out: AgendaProfile[] = [];
    const seen = new Set<string>();
    rows.forEach(r => {
      if (!r.profile || r.profile.ativo === false) return;
      if (seen.has(r.profile_id)) return;
      seen.add(r.profile_id);
      out.push(r.profile);
    });
    return out.sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
  };

  listUsersByRole = async (workspaceId: string | null, role: string): Promise<AgendaProfile[]> => {
    if (!workspaceId) return [];
    const rows = await this.repo.listTimeMembersByRole(workspaceId, role);
    const seen = new Set<string>();
    const out: AgendaProfile[] = [];
    rows.forEach(r => {
      if (!r.profiles || seen.has(r.user_id)) return;
      // Exclui profiles desativados (ativo=false). NULL/undefined passa.
      if (r.profiles.ativo === false) return;
      seen.add(r.user_id);
      out.push(r.profiles);
    });
    return out;
  };

  listWorkspaceMembers = async (workspaceId: string | null): Promise<AgendaProfile[]> => {
    if (!workspaceId) return [];
    const rows = await this.repo.listWorkspaceMemberRows(workspaceId);
    const seen = new Set<string>();
    const out: AgendaProfile[] = [];
    rows.forEach(r => {
      if (!r.profiles || seen.has(r.user_id)) return;
      seen.add(r.user_id);
      out.push({ ...r.profiles, workspace_role: r.role, atende_comercial: !!r.atende_comercial });
    });
    return out;
  };

  listSdrs = async (workspaceId: string | null, pipelineId: string | null = null): Promise<AgendaProfile[]> => {
    if (pipelineId) return this.listProfilesByPipelineRoleScoped(pipelineId, 'sdr');
    return this.listUsersByRole(workspaceId, 'sdr');
  };

  listClosers = async (workspaceId: string | null, pipelineId: string | null = null): Promise<AgendaProfile[]> => {
    if (!workspaceId) return [];
    if (pipelineId) {
      const profiles = await this.listProfilesByPipelineRoleScoped(pipelineId, 'closer');
      const scheduleSet = new Set(await this.repo.listActiveCloserScheduleIds(workspaceId));
      return profiles.map(p => ({ ...p, has_schedule: scheduleSet.has(p.id) }));
    }
    // Sem pipelineId — elegíveis do workspace: quem é closer de fato
    // (time_members.role = 'closer') OU quem está marcado como
    // `atende_comercial` (ex.: gestor que também atende, ver Config/74).
    // Gestores comuns (sem o flag) NÃO entram — a reunião deve ser conduzida
    // por um Closer/Comercial.
    const [members, closerTimeMembers, scheduleIds] = await Promise.all([
      this.listWorkspaceMembers(workspaceId),
      this.listUsersByRole(workspaceId, 'closer'),
      this.repo.listActiveCloserScheduleIds(workspaceId),
    ]);
    const closerSet = new Set((closerTimeMembers || []).map(p => p.id));
    const scheduleSet = new Set(scheduleIds);
    return (members || [])
      .filter(member => closerSet.has(member.id) || member.atende_comercial === true)
      .map(member => ({ ...member, has_schedule: scheduleSet.has(member.id) }))
      .sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
  };

  // ── Reuniões da semana ──────────────────────────────────────
  getMeetingsByWeek = async (params: GetMeetingsByWeekParams): Promise<AgendaMeeting[]> => {
    const { workspaceId, startISO, endISO, sdrId = null } = params;
    if (!workspaceId || !startISO || !endISO) return [];
    const enriched = await this.repo.getMeetingsByWeek(params);

    // Esconde reuniões de cards removidos da agenda (ativo=false).
    const visible = enriched.filter(m =>
      m.card?.linked_card?.ativo !== false && m.card?.ativo !== false
    );

    // Filtro SDR: usa lead.lead_sdr com fallback para meeting.sdr.
    if (sdrId) {
      return visible.filter(m => (m.lead?.lead_sdr?.id || m.sdr?.id) === sdrId);
    }
    return visible;
  };

  // ── Slots disponíveis (RPC) ─────────────────────────────────
  getAvailableSlots = async (params: GetAvailableSlotsParams): Promise<AvailableSlot[]> => {
    if (!params.closerId || !params.date) return [];
    return this.repo.getAvailableSlots(params);
  };

  // ── Agendar reunião (RPC transacional) ──────────────────────
  scheduleMeeting = async (params: ScheduleMeetingParams): Promise<string> => {
    const { leadId, sdrCardId, closerId, closerPipeId, scheduledAt } = params;
    if (!leadId || !sdrCardId || !closerId || !closerPipeId || !scheduledAt) {
      throw new Error('Parâmetros obrigatórios ausentes em scheduleMeeting.');
    }
    return this.repo.scheduleMeeting(params);
  };

  updateMeetingLink = async (params: UpdateMeetingLinkParams): Promise<boolean> => {
    if (!params.meetingId || !params.link) return false;
    return this.repo.updateMeetingLink(params);
  };

  editMeetingSchedule = async (params: EditMeetingScheduleParams): Promise<boolean> => {
    if (!params.meetingId || !params.scheduledAt) throw new Error('meetingId e scheduledAt obrigatórios.');
    return this.repo.editMeetingSchedule(params);
  };

  swapMeetingCloser = async (params: SwapMeetingCloserParams): Promise<false | SwapMeetingCloserResult> => {
    if (!params.meetingId || !params.newCloserId) throw new Error('meetingId e newCloserId obrigatórios.');
    return this.repo.swapMeetingCloser(params);
  };

  clearCardReagend = async (cardId: string | null): Promise<boolean> => {
    if (!cardId) return false;
    return this.repo.clearCardReagend(cardId);
  };

  // Ordem da reunião por lead (1ª, 2ª, 3ª…), por ordem de marcação.
  getMeetingOrdinals = async (leadIds: Array<string | null | undefined> | null | undefined): Promise<Record<string, number>> => {
    const ids = [...new Set((leadIds || []).filter(Boolean))] as string[];
    if (!ids.length) return {};
    const rows = await this.repo.listMeetingsForOrdinals(ids);
    const counts: Record<string, number> = {};
    const ordinals: Record<string, number> = {};
    rows.forEach(m => {
      counts[m.lead_id] = (counts[m.lead_id] || 0) + 1;
      ordinals[m.id] = counts[m.lead_id];
    });
    return ordinals; // { [meetingId]: ordinal }
  };

  discardReschedule = async (params: DiscardRescheduleParams): Promise<boolean> => {
    if (!params.sdrCardId && !params.closerCardId) {
      throw new Error('Nenhum card associado para remover.');
    }
    return this.repo.discardReschedule(params);
  };

  handoffLeadToAgenda = async (params: HandoffLeadToAgendaParams): Promise<Json> => {
    const { leadId, sdrCardId, closerId, closerPipeId } = params;
    if (!leadId || !sdrCardId || !closerId || !closerPipeId) {
      throw new Error('Parâmetros obrigatórios ausentes em handoffLeadToAgenda.');
    }
    return this.repo.handoffLeadToAgenda(params);
  };

  getPendingAgendaMeetings = async (params: PendingAgendaMeetingsParams): Promise<AgendaMeeting[]> => {
    if (!params.workspaceId) return [];
    return this.repo.getPendingAgendaMeetings(params);
  };

  getPendingReschedules = async (params: PendingReschedulesParams): Promise<PendingRescheduleRow[]> => {
    if (!params.workspaceId) return [];
    return this.repo.getPendingReschedules(params);
  };

  // ── Confirmar presença D-1 ──────────────────────────────────
  confirmPresence = async (params: ConfirmPresenceParams): Promise<boolean> => {
    if (!params.meetingId) throw new Error('meetingId obrigatório.');
    return this.repo.confirmPresence(params);
  };

  // ── Cancelar reunião ────────────────────────────────────────
  cancelMeeting = async (params: CancelMeetingParams): Promise<boolean> => {
    if (!params.meetingId) throw new Error('meetingId obrigatório.');
    return this.repo.cancelMeeting(params);
  };

  // ── No show ─────────────────────────────────────────────────
  registerNoShow = async (params: RegisterNoShowParams): Promise<boolean> => {
    if (!params.meetingId) throw new Error('meetingId obrigatório.');
    return this.repo.registerNoShow(params);
  };

  // ── Realizada (resultado registrado pelo Closer) ────────────
  registerResult = async (params: RegisterResultParams): Promise<boolean> => {
    if (!params.meetingId) throw new Error('meetingId obrigatório.');
    if (!params.result?.trim()) throw new Error('Resultado obrigatório.');
    return this.repo.registerResult(params);
  };

  updateMeetingStatusDirect = async (params: UpdateMeetingStatusDirectParams): Promise<boolean> => {
    if (!params.meetingId || !params.newStatus) throw new Error('meetingId e newStatus obrigatórios.');
    return this.repo.updateMeetingStatusDirect(params);
  };

  // ── Disponibilidade — leitura ───────────────────────────────
  getCloserBusyMeetings = (closerId: string, startISO: string, endISO: string) => {
    if (!closerId) return Promise.resolve([]);
    return this.repo.getCloserBusyMeetings(closerId, startISO, endISO);
  };

  // Dupla validação de disponibilidade: consulta a agenda do Google (todos os
  // workspaces) via webhook n8n. Falha de rede/erro retorna [] (não bloqueia).
  getCloserGoogleBusy = (closerId: string, startISO: string, endISO: string) => {
    if (!closerId) return Promise.resolve([]);
    return this.repo.getCloserGoogleBusy(closerId, startISO, endISO);
  };

  resetMeetingToScheduled = (meetingId: string, sdrCardId: string | null = null): Promise<void> => {
    if (!meetingId) throw new Error('meetingId e obrigatorio.');
    return this.repo.resetMeetingToScheduled(meetingId, sdrCardId);
  };

  getCloserSchedule = async (closerId: string | null): Promise<CloserScheduleRow[]> => {
    if (!closerId) return [];
    return this.repo.getCloserSchedule(closerId);
  };

  getBlockedDates = async (closerId: string | null): Promise<BlockedDateRow[]> => {
    if (!closerId) return [];
    return this.repo.getBlockedDates(closerId);
  };

  // ── Disponibilidade — escrita ───────────────────────────────
  upsertCloserSchedule = (row: CloserScheduleInsert): Promise<CloserScheduleRow | null> => {
    return this.repo.upsertCloserSchedule(row);
  };

  upsertBlockedDate = (row: BlockedDateInsert): Promise<BlockedDateRow | null> => {
    return this.repo.upsertBlockedDate(row);
  };

  deleteBlockedDate = (id: string): Promise<boolean> => {
    return this.repo.deleteBlockedDate(id);
  };

  // ── Permissões ──────────────────────────────────────────────
  getSchedulePerms = async (ownerId: string | null): Promise<SchedulePermWithEditor[]> => {
    if (!ownerId) return [];
    return this.repo.getSchedulePerms(ownerId);
  };

  addSchedulePerm = (params: AddSchedulePermParams): Promise<SchedulePermRow | null> => {
    return this.repo.addSchedulePerm(params);
  };

  removeSchedulePerm = (id: string): Promise<boolean> => {
    return this.repo.removeSchedulePerm(id);
  };

  // ── Config (chaves do módulo Agenda) ────────────────────────
  getAgendaConfig = async (workspaceId: string | null): Promise<AgendaConfig> => {
    if (!workspaceId) return { meetingDuration: 60, breakBetween: 15, slotInterval: 30 };
    const rows = await this.repo.getConfigValues(workspaceId, ['agenda_meeting_duration', 'agenda_break_between', 'agenda_slot_interval']);
    const map: Record<string, Json> = {};
    rows.forEach(r => { map[r.key] = r.value; });
    return {
      meetingDuration: Number(map.agenda_meeting_duration) || 60,
      breakBetween:    Number(map.agenda_break_between)    || 15,
      slotInterval:    Number(map.agenda_slot_interval)    || 30,
    };
  };

  listSdrCardsForLead = async (leadId: string | null): Promise<SdrCardForLead[]> => {
    if (!leadId) return [];
    const rows = await this.repo.listSdrCardRowsForLead(leadId);
    // Apenas cards de pipelines SDR.
    return rows.filter(c => c.pipeline?.role === 'sdr');
  };

  // Fechamentos (cards.status='won') do mês corrente, com faturamento.
  getWonCardsThisMonth = async (workspaceId: string | null): Promise<WonCard[]> => {
    if (!workspaceId) return [];
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const end   = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
    const rows = await this.repo.getWonCards(start, end);
    // O workspace é indireto (pipeline.workspace_id) — filtro no cliente.
    return rows.filter(c => c.pipeline?.workspace_id === workspaceId);
  };

  // Horários fixos (config por pipeline com fallback global e default).
  getSlotTimes = async (workspaceId: string | null, pipelineId: string | null = null): Promise<string[]> => {
    if (!workspaceId) return SLOT_TIMES_DEFAULT;
    const v = await this.readConfigWithPipelineOverride(workspaceId, 'agenda_slot_times', pipelineId);
    return Array.isArray(v) && v.length ? (v as string[]) : SLOT_TIMES_DEFAULT;
  };

  getPipelineMeetingDuration = async (workspaceId: string | null, pipelineId: string | null = null): Promise<number> => {
    if (!workspaceId) return 60;
    const v = await this.readConfigWithPipelineOverride(workspaceId, 'agenda_meeting_duration', pipelineId);
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 60;
  };

  setPipelineMeetingDuration = async (workspaceId: string | null, durationMin: unknown, pipelineId: string | null = null): Promise<number> => {
    const n = Number(durationMin);
    const val = Number.isFinite(n) && n > 0 ? n : 60;
    const key = pipelineId ? `agenda_meeting_duration:${pipelineId}` : 'agenda_meeting_duration';
    await this.setAgendaConfig(workspaceId, key, val);
    return val;
  };

  setSlotTimes = async (workspaceId: string | null, times: Array<string | null | undefined> | null | undefined, pipelineId: string | null = null): Promise<string[]> => {
    const normalized = [...new Set((times || [])
      .filter(Boolean)
      .map(t => String(t).slice(0, 5))
      .filter(t => /^\d{2}:\d{2}$/.test(t)))]
      .sort();
    const key = pipelineId ? `agenda_slot_times:${pipelineId}` : 'agenda_slot_times';
    await this.setAgendaConfig(workspaceId, key, normalized);
    return normalized;
  };

  listCloserPipelines = async (workspaceId: string | null): Promise<CloserPipeline[]> => {
    if (!workspaceId) return [];
    return this.repo.listCloserPipelines(workspaceId);
  };

  findCloserPipeline = (params: FindCloserPipelineParams): Promise<PipelineRow | null> => {
    // Guard defensivo — o adapter também retorna null sem workspaceId.
    if (!params.workspaceId) return Promise.resolve(null);
    return this.repo.findCloserPipeline(params);
  };
}
